import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Box, Button, type IIconsVector, Tooltip, TooltipLink, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { PresetCard, PresetGrid } from 'shared/Preset'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { NumbersPath } from 'entities/ExportRouters'
import { type IAddNumberType } from 'pages/settings/lib/addNumberType'
import styles from './styles.module.scss'

type IAddNumberInfo = {
  key: IAddNumberType
  icon: IIconsVector
  title: string
  subtitle: string
  isDisabled?: boolean
}

type IHowAddNumberCardsProps = {
  setAddNumberType: (addNumberType: IAddNumberType) => void
  nextClickHandler?: () => void
  unassignedNumbersCount: number
  isGracePeriod?: boolean
  hiddenKeys?: string[]
  isModalMode?: boolean
  selectedType?: IAddNumberType
}

const gracePeriodTooltipLabel = (
  <Box display='flex' flexDirection='column' gap={0.5}>
    <Typography
      variant={'body-md-regular'}
      color={'var(--content-primary-primary-inverted)'}
      ariaLabel={'Grace Period'}
    >
      Take care of unpaid licenses
      <br /> before adding new numbers.
    </Typography>
  </Box>
)

const gracePeriodTooltipDesc = (
  <TooltipLink
    text='Manage numbers'
    onClick={() => {
      uiStore.changeRoute({ path: `${NumbersPath.root}` })
    }}
  />
)

export const HowAddNumberCards = observer(
  ({
    setAddNumberType,
    nextClickHandler,
    unassignedNumbersCount,
    isGracePeriod,
    hiddenKeys,
    isModalMode = false,
    selectedType,
  }: IHowAddNumberCardsProps) => {
    const { uk_compliance } = useEntitiesContext().featureFlagsStore

    const addNumberInfo: IAddNumberInfo[] = [
      {
        key: 'assign',
        icon: 'phoneTransfer',
        title: 'Assign a number',
        subtitle: `Your workspace has ${unassignedNumbersCount} unassigned number${
          unassignedNumbersCount === 1 ? '' : 's'
        } available`,
        isDisabled: unassignedNumbersCount === 0,
      },
      {
        key: 'buy',
        icon: 'shoppingCart',
        title: 'Buy a number',
        subtitle: uk_compliance
          ? 'Get a local, toll-free or international number'
          : 'Get a local or toll-free number',
        isDisabled: isGracePeriod,
      },
      {
        key: 'app',
        icon: 'plug',
        title: 'Add a number from an app',
        subtitle: 'Connect a number from calling apps (Aircall, RingCentral, etc.)',
        isDisabled: isGracePeriod,
      },
      {
        key: 'landline',
        icon: 'phone',
        title: 'Text-enable a landline',
        subtitle: 'Send and receive text messages with your landline',
        isDisabled: isGracePeriod,
      },
    ]

    const visibleItems = addNumberInfo.filter((item) => !hiddenKeys?.includes(item.key))
    const isAllDisabled = visibleItems.every((item) => item.isDisabled)

    return (
      <div className={classNames(styles.content, { [styles.modalMode]: isModalMode })}>
        <PresetGrid columns={2} className={styles.grid}>
          {visibleItems.map((item) => (
            <Tooltip
              key={item.key}
              type='description'
              label={gracePeriodTooltipLabel}
              placement={'top'}
              width={233}
              fullWidth
              withoutPointer
              visibility={isGracePeriod && item.key !== 'assign' ? 'visible' : 'hidden'}
              desc={gracePeriodTooltipDesc}
            >
              <PresetCard
                icon={item.icon}
                name={item.title}
                description={item.subtitle}
                disabled={item.isDisabled}
                selected={selectedType === item.key}
                onClick={() => setAddNumberType(item.key)}
              />
            </Tooltip>
          ))}
        </PresetGrid>
        {!isModalMode && nextClickHandler && (
          <Button
            typeBtn='contained'
            size='medium'
            text='Next'
            fullWidth
            disabled={isAllDisabled}
            onClick={nextClickHandler}
          />
        )}
      </div>
    )
  }
)
